import { assign, fromCallback, fromPromise, setup } from 'xstate';
import type { AnyActorRef } from 'xstate';
import { firstValueFrom } from 'rxjs';

import type { EstimatedPickupTime } from '@/data/models/reservation.model';
import reservationService from '@/services/reservationService';

import type { TripArrayFieldContext } from './createTripArrayFieldMachine';

/** Subset of Google Directions results used to populate travel time UI. */
export type DirectionsResultLike = {
  routes?: Array<{
    legs?: Array<{
      duration?: { text?: string };
      distance?: { text?: string };
    }>;
  }>;
};

export type GetDirectionsFn = (
  origin: string,
  destination: string,
) => Promise<DirectionsResultLike | undefined>;

export type DirectionsTravelDisplay = {
  durationText: string;
  distanceText: string;
};

export type TripLegEstimatedPickupInput = {
  pickupAddressRef: AnyActorRef;
  dropoffAddressRef: AnyActorRef;
  /** Which trip leg row to read from the pickup/dropoff array machines (default 0). */
  tripIndex?: number;
  /** From `GoogleMapsContext`; used to populate travel time UI for the first leg. */
  getDirections?: GetDirectionsFn;
};

export type TripLegEstimatedPickupContext = {
  pickupAddressRef: AnyActorRef;
  dropoffAddressRef: AnyActorRef;
  tripIndex: number;
  estimatedPickupTime: EstimatedPickupTime | null;
  /** Human-readable driving time/distance from Google Directions (first leg), when available. */
  directionsTravel: DirectionsTravelDisplay | null;
  getDirections?: GetDirectionsFn;
  /** Set while a request is in flight; used to ignore duplicate address updates. */
  pendingRequestKey: string | null;
  /** Key for the last successful `getEstimatedTripTime` response. */
  lastFetchedKey: string | null;
  fetchError: unknown | null;
};

function readTripArrayValues(ref: AnyActorRef): unknown[] {
  const ctx = ref.getSnapshot().context as TripArrayFieldContext;
  return ctx.values.length ? ctx.values : [''];
}

/** Aligns with trip leg address machines / TripLegForm address shapes. */
function addressMachineValueToLineString(val: unknown): string {
  if (val == null) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'object') {
    const p = val as {
      isHeader?: boolean;
      description?: string;
      mainText?: string;
      secondaryText?: string;
      zipCode?: string;
    };
    if (p.isHeader) return p.description ?? '';
    if (p.mainText) {
      const street = p.mainText;
      let city = p.secondaryText || '';
      const zip = p.zipCode;
      if (zip) city = city.replace(` ${zip}`, '');
      return `${street} ${city} ${zip || ''}`.trim();
    }
    return `${p.description ?? ''} ${p.zipCode ?? ''}`.trim();
  }
  return '';
}

function getPairAtIndex(
  pickupRef: AnyActorRef,
  dropoffRef: AnyActorRef,
  tripIndex: number,
): { pickup: unknown; dropoff: unknown; pickupLine: string; dropoffLine: string } {
  const pickups = readTripArrayValues(pickupRef);
  const dropoffs = readTripArrayValues(dropoffRef);
  const pickup = pickups[tripIndex] ?? pickups[0];
  const dropoff = dropoffs[tripIndex] ?? dropoffs[0];
  return {
    pickup,
    dropoff,
    pickupLine: addressMachineValueToLineString(pickup),
    dropoffLine: addressMachineValueToLineString(dropoff),
  };
}

function pairKey(pickupLine: string, dropoffLine: string): string {
  return `${pickupLine}\0${dropoffLine}`;
}

/** Matches TripLegForm: `pickupAddress.description` / `dropoffAddress.description` when present. */
function toApiAddresses(pickup: unknown, dropoff: unknown): {
  pickUpAddress: unknown;
  dropOffAddress: unknown;
} {
  const pickUpAddress =
    pickup != null &&
    typeof pickup === 'object' &&
    'description' in pickup &&
    typeof (pickup as { description?: unknown }).description === 'string'
      ? (pickup as { description: string }).description
      : addressMachineValueToLineString(pickup);
  const dropOffAddress =
    dropoff != null &&
    typeof dropoff === 'object' &&
    'description' in dropoff &&
    typeof (dropoff as { description?: unknown }).description === 'string'
      ? (dropoff as { description: string }).description
      : addressMachineValueToLineString(dropoff);
  return { pickUpAddress, dropOffAddress };
}

const addressPairSubscription = fromCallback(
  ({
    sendBack,
    input,
  }: {
    sendBack: (event: { type: 'ADDRESSES_UPDATED' }) => void;
    input: Pick<
      TripLegEstimatedPickupContext,
      'pickupAddressRef' | 'dropoffAddressRef'
    >;
  }) => {
    const emit = () => {
      sendBack({ type: 'ADDRESSES_UPDATED' });
    };
    const sub1 = input.pickupAddressRef.subscribe(emit);
    const sub2 = input.dropoffAddressRef.subscribe(emit);
    emit();
    return () => {
      sub1.unsubscribe();
      sub2.unsubscribe();
    };
  },
);

export const tripLegEstimatedPickupMachine = setup({
  types: {
    context: {} as TripLegEstimatedPickupContext,
    events: {} as
      | { type: 'ADDRESSES_UPDATED' }
      | { type: 'RESET' },
    input: {} as TripLegEstimatedPickupInput,
  },
  actors: {
    addressPairSubscription,
    fetchTripLegEstimates: fromPromise<
      {
        estimatedPickupTime: EstimatedPickupTime;
        directionsTravel: DirectionsTravelDisplay | null;
      },
      {
        pickUpAddress: unknown;
        dropOffAddress: unknown;
        getDirections?: GetDirectionsFn;
      }
    >(async ({ input }) => {
      const estimatedPickupTime = await firstValueFrom(
        reservationService.getEstimatedTripTime(
          input.pickUpAddress,
          input.dropOffAddress,
        ),
      );

      const { pickUpAddress, dropOffAddress, getDirections } = input;
      const pickupStr = String(pickUpAddress ?? '').trim();
      const dropoffStr = String(dropOffAddress ?? '').trim();

      let directionsTravel: DirectionsTravelDisplay | null = null;
      if (getDirections && pickupStr && dropoffStr) {
        try {
          const dir = await getDirections(pickupStr, dropoffStr);
          const leg = dir?.routes?.[0]?.legs?.[0];
          const durationText = leg?.duration?.text;
          const distanceText = leg?.distance?.text;
          if (durationText && distanceText) {
            directionsTravel = { durationText, distanceText };
          }
        } catch {
          // Directions are optional; server estimate still applies.
        }
      }

      return { estimatedPickupTime, directionsTravel };
    }),
  },
}).createMachine({
  id: 'tripLegEstimatedPickup',
  context: ({ input }) => ({
    pickupAddressRef: input.pickupAddressRef,
    dropoffAddressRef: input.dropoffAddressRef,
    tripIndex: input.tripIndex ?? 0,
    estimatedPickupTime: null,
    directionsTravel: null,
    getDirections: input.getDirections,
    pendingRequestKey: null,
    lastFetchedKey: null,
    fetchError: null,
  }),
  initial: 'active',
  states: {
    active: {
      invoke: {
        id: 'addressPairSubscription',
        src: 'addressPairSubscription',
        input: ({ context }) => ({
          pickupAddressRef: context.pickupAddressRef,
          dropoffAddressRef: context.dropoffAddressRef,
        }),
      },
      initial: 'ready',
      states: {
        ready: {
          on: {
            ADDRESSES_UPDATED: [
              {
                guard: ({ context }) => {
                  const { pickupLine, dropoffLine } = getPairAtIndex(
                    context.pickupAddressRef,
                    context.dropoffAddressRef,
                    context.tripIndex,
                  );
                  return !pickupLine.trim() || !dropoffLine.trim();
                },
                actions: assign({
                  estimatedPickupTime: () => null,
                  directionsTravel: () => null,
                  fetchError: () => null,
                  lastFetchedKey: () => null,
                  pendingRequestKey: () => null,
                }),
              },
              {
                guard: ({ context }) => {
                  const { pickupLine, dropoffLine } = getPairAtIndex(
                    context.pickupAddressRef,
                    context.dropoffAddressRef,
                    context.tripIndex,
                  );
                  if (!pickupLine.trim() || !dropoffLine.trim()) return false;
                  const key = pairKey(pickupLine, dropoffLine);
                  return key !== context.lastFetchedKey;
                },
                target: 'fetching',
              },
            ],
            RESET: {
              actions: assign({
                estimatedPickupTime: () => null,
                directionsTravel: () => null,
                fetchError: () => null,
                lastFetchedKey: () => null,
                pendingRequestKey: () => null,
              }),
            },
          },
        },
        fetching: {
          entry: assign({
            pendingRequestKey: ({ context }) => {
              const { pickupLine, dropoffLine } = getPairAtIndex(
                context.pickupAddressRef,
                context.dropoffAddressRef,
                context.tripIndex,
              );
              return pairKey(pickupLine, dropoffLine);
            },
            fetchError: () => null,
          }),
          invoke: {
            id: 'fetchTripLegEstimates',
            src: 'fetchTripLegEstimates',
            input: ({ context }) => {
              const { pickup, dropoff } = getPairAtIndex(
                context.pickupAddressRef,
                context.dropoffAddressRef,
                context.tripIndex,
              );
              return {
                ...toApiAddresses(pickup, dropoff),
                getDirections: context.getDirections,
              };
            },
            onDone: {
              target: 'ready',
              actions: assign(({ context, event }) => {
                const output = event.output as {
                  estimatedPickupTime: EstimatedPickupTime;
                  directionsTravel: DirectionsTravelDisplay | null;
                };
                const { pickupLine, dropoffLine } = getPairAtIndex(
                  context.pickupAddressRef,
                  context.dropoffAddressRef,
                  context.tripIndex,
                );
                return {
                  estimatedPickupTime: output.estimatedPickupTime,
                  directionsTravel: output.directionsTravel,
                  fetchError: null,
                  lastFetchedKey: pairKey(pickupLine, dropoffLine),
                  pendingRequestKey: null,
                };
              }),
            },
            onError: {
              target: 'ready',
              actions: assign({
                estimatedPickupTime: () => null,
                directionsTravel: () => null,
                fetchError: ({ event }) => event.error,
                pendingRequestKey: () => null,
              }),
            },
          },
          on: {
            ADDRESSES_UPDATED: [
              {
                guard: ({ context }) => {
                  const { pickupLine, dropoffLine } = getPairAtIndex(
                    context.pickupAddressRef,
                    context.dropoffAddressRef,
                    context.tripIndex,
                  );
                  return !pickupLine.trim() || !dropoffLine.trim();
                },
                target: 'ready',
                actions: assign({
                  estimatedPickupTime: () => null,
                  directionsTravel: () => null,
                  fetchError: () => null,
                  lastFetchedKey: () => null,
                  pendingRequestKey: () => null,
                }),
              },
              {
                guard: ({ context }) => {
                  const { pickupLine, dropoffLine } = getPairAtIndex(
                    context.pickupAddressRef,
                    context.dropoffAddressRef,
                    context.tripIndex,
                  );
                  if (!pickupLine.trim() || !dropoffLine.trim()) return false;
                  const key = pairKey(pickupLine, dropoffLine);
                  return (
                    context.pendingRequestKey != null &&
                    key !== context.pendingRequestKey
                  );
                },
                target: 'fetching',
                reenter: true,
              },
            ],
            RESET: {
              target: 'ready',
              actions: assign({
                estimatedPickupTime: () => null,
                directionsTravel: () => null,
                fetchError: () => null,
                lastFetchedKey: () => null,
                pendingRequestKey: () => null,
              }),
            },
          },
        },
      },
      on: {
        RESET: {
          actions: assign({
            estimatedPickupTime: () => null,
            directionsTravel: () => null,
            fetchError: () => null,
            lastFetchedKey: () => null,
            pendingRequestKey: () => null,
          }),
        },
      },
    },
  },
});
